/**
 * Das Profilbild in der Oberfläche: verkleinern vor dem Senden, laden mit Token.
 *
 * Zwei Dinge, die man beim ersten Hinsehen nicht erwartet, und beide folgen aus
 * ADR 0016 und ADR 0005:
 *
 * 1. **Verkleinert wird hier**, nicht auf dem Server. Ein 8-MB-Handyfoto
 *    verlässt das Gerät gar nicht erst, und die API braucht keinen Bildparser,
 *    der fremde Bytes durchgeht. Der Server prüft trotzdem Größe und Signatur —
 *    er wird entlastet, nicht ersetzt.
 * 2. **Geladen wird mit `fetch`**, nicht über `<img src>`. Der Endpunkt verlangt
 *    einen `Authorization`-Kopf, und den schickt ein Bildaufruf des Browsers
 *    nicht mit. Das ist die Folge davon, dass der Zugriffstoken im Speicher
 *    liegt und nicht in einem Cookie.
 */
import { useEffect, useState } from "react";
import { accessToken } from "./token-store.js";
import { apiBaseUrl } from "./client.js";

/** Was der Vertrag annimmt — die Auswahl im Dateidialog richtet sich danach. */
export const ACCEPTED_IMAGE_TYPES = "image/png,image/jpeg,image/webp";

/**
 * Kantenlänge des fertigen Bildes.
 *
 * Steht **hier** und nicht in `@hausmeter/domain`: Der Server kennt keine Pixel.
 * Er prüft Größe in Bytes und die Signatur der Datei; wie groß das Bild dabei
 * herauskommt, ist die Entscheidung dessen, der es sendet. Eine gemeinsame
 * Konstante täuschte eine Absprache vor, die es nicht gibt.
 */
const EDGE_PIXELS = 256;

export interface PreparedAvatar {
  readonly data: string;
  readonly mimeType: "image/webp";
}

/**
 * Bringt eine gewählte Datei auf 256×256 und gibt sie Base64-kodiert zurück.
 *
 * **Quadratisch zugeschnitten, nicht verzerrt.** Aus der Mitte wird das größte
 * Quadrat genommen; ein auf quadratisch gestauchtes Hochformat sieht falsch aus,
 * und niemand erkennt den Grund.
 */
export async function prepareAvatar(file: File): Promise<PreparedAvatar> {
  const bitmap = await createImageBitmap(file);

  try {
    const edge = Math.min(bitmap.width, bitmap.height);
    const canvas = document.createElement("canvas");
    canvas.width = EDGE_PIXELS;
    canvas.height = EDGE_PIXELS;

    const context = canvas.getContext("2d");
    if (context === null) throw new Error("Der Browser stellt keine Zeichenfläche bereit.");

    context.drawImage(
      bitmap,
      (bitmap.width - edge) / 2,
      (bitmap.height - edge) / 2,
      edge,
      edge,
      0,
      0,
      EDGE_PIXELS,
      EDGE_PIXELS,
    );

    const blob = await new Promise<Blob | null>((resolve) =>
      canvas.toBlob(resolve, "image/webp", 0.85),
    );

    /**
     * `toBlob` gibt `null` zurück, wenn es den Typ nicht kann. Kein stiller
     * Rückfall auf PNG: Was das Ergebnis ist, soll nicht davon abhängen, welcher
     * Browser gerade läuft (Regel 9).
     */
    if (blob === null) throw new Error("Das Bild konnte nicht in WebP umgewandelt werden.");

    return { data: await toBase64(blob), mimeType: "image/webp" };
  } finally {
    // Ein `ImageBitmap` hält Speicher außerhalb des Heaps; ohne `close` gibt der
    // Browser ihn erst irgendwann frei.
    bitmap.close();
  }
}

/**
 * Lädt das Profilbild und gibt eine Objekt-URL zurück — oder `null`, wenn keines
 * hinterlegt ist.
 *
 * **Der Aufrufer muss die URL wieder freigeben** (`URL.revokeObjectURL`).
 * Andernfalls hält der Browser jedes je geladene Bild bis zum Neuladen der
 * Seite fest.
 */
export async function loadAvatarUrl(): Promise<string | null> {
  const token = accessToken();
  if (token === null) return null;

  const response = await fetch(`${apiBaseUrl}/auth/me/avatar`, {
    headers: { Authorization: `Bearer ${token}` },
    credentials: "include",
  });

  // `404` heißt „hat keins" und ist kein Fehler — es ist die häufigste Antwort
  // dieses Endpunkts.
  if (!response.ok) return null;

  return URL.createObjectURL(await response.blob());
}

/**
 * Das Profilbild als Objekt-URL — oder `null`, solange oder weil es keines gibt.
 *
 * **`avatarUpdatedAt` ist die Abhängigkeit, nicht die Nutzerkennung.** Der
 * Zeitstempel ändert sich bei jedem Hochladen und ist damit das Signal, das alte
 * Bild zu vergessen; ohne ihn zeigte der Browser nach dem Ersetzen weiter das
 * vorige. `null` heißt „kein Bild" und spart den Aufruf ganz.
 *
 * Der Hook gibt die URL beim Aufräumen wieder frei. Andernfalls hielte der
 * Browser jedes je geladene Bild bis zum Neuladen der Seite fest.
 */
export function useAvatarUrl(avatarUpdatedAt: string | null): string | null {
  const [url, setUrl] = useState<string | null>(null);

  useEffect(() => {
    if (avatarUpdatedAt === null) {
      setUrl(null);
      return;
    }

    let objectUrl: string | null = null;
    let abgebrochen = false;

    void loadAvatarUrl().then((loaded) => {
      objectUrl = loaded;
      // Wer währenddessen weggeklickt hat, bekommt keinen Zustand mehr gesetzt —
      // sonst schreibt React in eine Komponente, die es nicht mehr gibt.
      if (abgebrochen) return;
      setUrl(loaded);
    });

    return () => {
      abgebrochen = true;
      if (objectUrl !== null) URL.revokeObjectURL(objectUrl);
    };
  }, [avatarUpdatedAt]);

  return url;
}

function toBase64(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error("Die Datei konnte nicht gelesen werden."));
    reader.onload = () => {
      // `readAsDataURL` liefert „data:image/webp;base64,…". Der Vertrag will
      // den Teil dahinter — das Präfix wiederholt nur den `mimeType`, der
      // ohnehin daneben steht.
      const result = String(reader.result);
      resolve(result.slice(result.indexOf(",") + 1));
    };
    reader.readAsDataURL(blob);
  });
}
